import {
  FindingSeveritySchema,
  SignalStage,
  type FindingSeverityType,
  type SignalStageType,
} from '@terra/shared-schemas-orchestrator';
import type { TraceStatusType } from '@terra/shared-schemas-ai';
import type { FindingsGroupCount } from '@terra/shared-contracts-findings';
import {
  TRACE_STATUS_COLOR_TOKENS,
  type SegmentedCountBarItem,
} from '@terra/ui-design-system';

type PostureColorToken = SegmentedCountBarItem['colorToken'];

type PostureItemSeed<TKey extends string> = {
  key: TKey;
  colorToken: PostureColorToken;
  count: number;
};

export const FINDING_SEVERITY_POSTURE_ORDER = [
  FindingSeveritySchema.enum.Critical,
  FindingSeveritySchema.enum.High,
  FindingSeveritySchema.enum.Medium,
  FindingSeveritySchema.enum.Low,
  FindingSeveritySchema.enum.Info,
] as const satisfies readonly FindingSeverityType[];

const FINDING_SEVERITY_COLORS: Record<FindingSeverityType, PostureColorToken> =
  {
    Critical: 'colors.bg.graphs.critical',
    High: 'colors.bg.graphs.high',
    Medium: 'colors.bg.graphs.medium',
    Low: 'colors.bg.graphs.low',
    Info: 'colors.bg.graphs.gray',
  };

export const SIGNAL_STAGE_POSTURE_ORDER = [
  SignalStage.NEW,
  SignalStage.IN_TESTING,
  SignalStage.NEEDS_REVIEW,
  SignalStage.TESTED,
] as const satisfies readonly SignalStageType[];

const SIGNAL_STAGE_COLORS: Record<SignalStageType, PostureColorToken> = {
  NEW: 'colors.bg.graphs.gray',
  IN_TESTING: 'colors.bg.graphs.purple',
  NEEDS_REVIEW: 'colors.bg.graphs.yellow',
  TESTED: 'colors.bg.graphs.green',
};

export const EXECUTION_OUTCOME_POSTURE_ORDER = [
  'COMPLETED',
  'RUNNING',
  'PAUSED',
  'BLOCKED',
  'FAILED',
  'ABORTED',
] as const satisfies readonly TraceStatusType[];

export type ExecutionOutcomeKey =
  (typeof EXECUTION_OUTCOME_POSTURE_ORDER)[number];

export const buildFindingSeverityPostureItems = ({
  counts,
}: {
  counts: Record<FindingSeverityType, number>;
}): PostureItemSeed<FindingSeverityType>[] =>
  FINDING_SEVERITY_POSTURE_ORDER.map((severity) => ({
    key: severity,
    colorToken: FINDING_SEVERITY_COLORS[severity],
    count: counts[severity],
  }));

export const toFindingSeverityCounts = (
  groups: readonly FindingsGroupCount[],
): Record<FindingSeverityType, number> =>
  groups.reduce(
    (counts, group) => {
      const severity = FindingSeveritySchema.safeParse(group.key);

      return severity.success
        ? { ...counts, [severity.data]: counts[severity.data] + group.count }
        : counts;
    },
    { Critical: 0, High: 0, Medium: 0, Low: 0, Info: 0 },
  );

export type SeverityBuckets = {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info?: number;
};

export const fromSeverityBuckets = ({
  critical,
  high,
  medium,
  low,
  info = 0,
}: SeverityBuckets): Record<FindingSeverityType, number> => ({
  Critical: critical,
  High: high,
  Medium: medium,
  Low: low,
  Info: info,
});

export const EXTERNAL_ASSET_POSTURE_ORDER = [
  'hosts',
  'domains',
  'services',
  'gateways',
  'netblocks',
  'software',
] as const;

export type ExternalAssetKind = (typeof EXTERNAL_ASSET_POSTURE_ORDER)[number];

const EXTERNAL_ASSET_COLORS: Record<ExternalAssetKind, PostureColorToken> = {
  hosts: 'colors.bg.graphs.sky',
  domains: 'colors.bg.graphs.purple',
  services: 'colors.bg.graphs.green',
  gateways: 'colors.bg.graphs.yellow',
  netblocks: 'colors.bg.graphs.gray',
  software: 'colors.bg.graphs.medium',
};

export const buildExternalAssetPostureItems = ({
  counts,
}: {
  counts: Record<ExternalAssetKind, number>;
}): PostureItemSeed<ExternalAssetKind>[] =>
  EXTERNAL_ASSET_POSTURE_ORDER.map((kind) => ({
    key: kind,
    colorToken: EXTERNAL_ASSET_COLORS[kind],
    count: counts[kind],
  })).filter((item) => item.count > 0);

export const buildSignalStagePostureItems = ({
  counts,
}: {
  counts: Record<SignalStageType, number>;
}): PostureItemSeed<SignalStageType>[] =>
  SIGNAL_STAGE_POSTURE_ORDER.map((stage) => ({
    key: stage,
    colorToken: SIGNAL_STAGE_COLORS[stage],
    count: counts[stage],
  }));

export const buildExecutionOutcomePostureItems = ({
  counts,
}: {
  counts: Record<ExecutionOutcomeKey, number>;
}): PostureItemSeed<ExecutionOutcomeKey>[] =>
  EXECUTION_OUTCOME_POSTURE_ORDER.map((outcome) => ({
    key: outcome,
    colorToken: TRACE_STATUS_COLOR_TOKENS[outcome],
    count: counts[outcome],
  })).filter((item) => item.count > 0);

export const sumPostureCounts = (
  items: ReadonlyArray<{ count: number }>,
): number => items.reduce((sum, item) => sum + item.count, 0);
